import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, X, Send, Loader2, ArrowRight, ScanLine, Siren,
  Swords, Building2, MessageSquare, Gavel, FileText, AlertTriangle
} from "lucide-react";
import api from "../lib/api";
import VoiceMicButton from "./VoiceMicButton";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";

const ACTION_META = {
  scan: { label: "Scanner le document", route: "/sos?action=scan&auto=1", icon: ScanLine, color: "bg-slate-900 text-[#B89C65]" },
  police: { label: "Contrôle police", route: "/sos?action=police", icon: Siren, color: "bg-red-600 text-white" },
  bagarre: { label: "Bagarre / Conflit", route: "/sos?action=bagarre", icon: Swords, color: "bg-orange-600 text-white" },
  bureau: { label: "Bureau officiel", route: "/sos?action=bureau", icon: Building2, color: "bg-slate-900 text-[#B89C65]" },
  chat: { label: "Chat juridique", route: "/dashboard", icon: MessageSquare, color: "bg-slate-900 text-[#B89C65]" },
  analyse: { label: "Qui a raison ?", route: "/analyses", icon: Gavel, color: "bg-slate-900 text-[#B89C65]" },
  document: { label: "Générer un document", route: "/documents", icon: FileText, color: "bg-slate-900 text-[#B89C65]" },
};

export default function AIHelper() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  // Don't show when not logged in (avoids confusion with install banner on landing)
  if (!user) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!description.trim() || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/triage", { description });
      setResult(res.data);
    } catch (err) {
      setResult({
        action: "chat",
        label: "Chat juridique",
        why_short: "Erreur de l'assistant — je vous redirige vers le chat",
        first_step: "Posez votre question à l'assistant juridique.",
        urgent: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGo = () => {
    if (!result) return;
    const meta = ACTION_META[result.action] || ACTION_META.chat;
    setOpen(false);
    setDescription("");
    setResult(null);
    navigate(meta.route);
  };

  const reset = () => {
    setResult(null);
    setDescription("");
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 sm:bottom-6 sm:right-24 z-50 group flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-sm pl-3 pr-4 py-3 shadow-2xl border border-slate-700 transition-all hover:scale-105"
          data-testid="ai-helper-launcher"
        >
          <span className="h-8 w-8 rounded-sm bg-[#B89C65] flex items-center justify-center">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="text-sm font-medium hidden sm:block">Que faire ?</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-0 sm:p-6 animate-fade-in-up" onClick={() => setOpen(false)}>
          <div
            className="w-full sm:max-w-lg bg-white rounded-t-sm sm:rounded-sm shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto custom-scroll"
            onClick={(e) => e.stopPropagation()}
            data-testid="ai-helper-panel"
          >
            {/* Header */}
            <div className="bg-slate-900 text-white p-5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm bg-[#B89C65] flex items-center justify-center">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65]">Assistant</p>
                <h3 className="font-serif-display text-lg font-medium">Que se passe-t-il ?</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="ai-helper-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            {!result && (
              <form onSubmit={handleSubmit} className="p-5">
                <p className="text-sm text-slate-600 mb-4">
                  Décrivez votre situation en 1-2 phrases. Je vous oriente vers la bonne fonction.
                </p>
                <div className="relative mb-3">
                  <Textarea
                    ref={textareaRef}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Ex: Un policier veut fouiller mon sac... ou : J'ai reçu un courrier d'huissier..."
                    className="rounded-sm pr-12 resize-none"
                    data-testid="ai-helper-input"
                  />
                  <div className="absolute top-2 right-2">
                    <VoiceMicButton currentValue={description} onTranscript={setDescription} testid="ai-helper-mic" />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={!description.trim() || loading}
                  className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11"
                  data-testid="ai-helper-submit"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" />Que dois-je faire ?</>
                  )}
                </Button>
                <SuggestionsRow onPick={(text) => setDescription(text)} />
              </form>
            )}

            {/* Result */}
            {result && <ResultBlock result={result} onGo={handleGo} onReset={reset} />}
          </div>
        </div>
      )}
    </>
  );
}

function SuggestionsRow({ onPick }) {
  const examples = [
    "Un policier veut me contrôler",
    "J'ai reçu un courrier d'huissier",
    "Mon propriétaire ne rend pas ma garantie",
    "Litige avec mon assureur",
  ];
  return (
    <div className="mt-4">
      <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Exemples</p>
      <div className="flex flex-wrap gap-1.5">
        {examples.map((ex, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPick(ex)}
            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm px-2.5 py-1.5 transition-colors"
            data-testid={`ai-helper-example-${i}`}
          >
            {ex}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultBlock({ result, onGo, onReset }) {
  const meta = ACTION_META[result.action] || ACTION_META.chat;
  const Icon = meta.icon;
  return (
    <div className="p-5" data-testid="ai-helper-result">
      {result.urgent && (
        <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-800">Situation urgente détectée</p>
        </div>
      )}

      <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Touche recommandée</p>
      <div className={`${meta.color} rounded-sm p-4 mb-4 flex items-center gap-3`}>
        <div className="h-10 w-10 rounded-sm bg-white/15 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="font-serif-display text-lg font-medium">{result.label || meta.label}</p>
          <p className="text-xs opacity-90 leading-relaxed">{result.why_short}</p>
        </div>
      </div>

      {result.first_step && (
        <div className="border-l-2 border-[#B89C65] bg-amber-50 rounded-sm p-3 mb-5">
          <p className="text-xs uppercase tracking-widest text-[#B89C65] mb-1">À faire maintenant</p>
          <p className="text-sm text-slate-800">{result.first_step}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          onClick={onGo}
          className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800 h-11"
          data-testid="ai-helper-go"
        >
          Y aller maintenant <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <Button
          onClick={onReset}
          variant="outline"
          className="rounded-sm h-11"
          data-testid="ai-helper-reset"
        >
          Reformuler
        </Button>
      </div>
    </div>
  );
}
